import { supabase } from '../lib/supabase';

/**
 * Fetch current budget data for the AR Scanner
 */
export async function fetchBudgetForScanner(userId) {
  const currentMonth = new Date()?.toISOString()?.slice(0, 7);

  const [budgetRes, categoriesRes] = await Promise.all([
    supabase?.from('budgets')?.select('*')?.eq('user_id', userId)?.eq('month_year', currentMonth)?.order('created_at', { ascending: false })?.limit(1)?.maybeSingle(),
    supabase?.from('spending_categories')?.select('*')?.eq('user_id', userId)?.order('monthly_limit', { ascending: false }),
  ]);

  const budget = budgetRes?.data;
  const categories = categoriesRes?.data || [];

  const totalSpent = categories?.reduce((s, c) => s + (parseFloat(c?.spent) || 0), 0);
  const totalBudget = parseFloat(budget?.total_budget) || 0;

  // Find shopping category or use first category
  const shoppingCat = categories?.find((c) => c?.name?.toLowerCase()?.includes('shop')) || categories?.[0];

  return {
    totalBudget,
    totalSpent,
    category: shoppingCat?.name || 'Shopping',
    categoryBudget: parseFloat(shoppingCat?.monthly_limit) || 400,
    categorySpent: parseFloat(shoppingCat?.spent) || 0,
    categoryId: shoppingCat?.id || null,
  };
}

/**
 * Log a purchase from AR Scanner and update category spent
 */
export async function logARPurchase(userId, product, categoryId) {
  const amount = parseFloat(product?.price) || 0;

  // Insert transaction
  const { error: txError } = await supabase?.from('transactions')?.insert({
      user_id: userId,
      category_id: categoryId || null,
      merchant: product?.name || 'AR Scanned Product',
      amount,
      transaction_type: 'expense',
      transaction_date: new Date()?.toISOString()?.slice(0, 10),
      notes: `Scanned via AR: ${product?.brand || ''}`,
      ar_scanned: true,
    });

  if (txError) throw txError;

  // Update category spent
  if (categoryId) {
    const { data: cat } = await supabase?.from('spending_categories')?.select('spent')?.eq('id', categoryId)?.single();

    if (cat) {
      await supabase?.from('spending_categories')?.update({ spent: (parseFloat(cat?.spent) || 0) + amount })?.eq('id', categoryId);
    }
  }
}

/**
 * Subscribe to real-time budget changes for a user
 * Returns unsubscribe function
 */
export function subscribeToBudgetUpdates(userId, onUpdate) {
  const channel = supabase?.channel(`budget_updates_${userId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'spending_categories',
        filter: `user_id=eq.${userId}`,
      },
      async () => {
        // Re-fetch fresh budget data on any change
        try {
          const data = await fetchBudgetForScanner(userId);
          onUpdate(data);
        } catch (err) {
          console.error('Real-time budget update error:', err);
        }
      }
    )?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'transactions',
        filter: `user_id=eq.${userId}`,
      },
      async () => {
        try {
          const data = await fetchBudgetForScanner(userId);
          onUpdate(data);
        } catch (err) {
          console.error('Real-time transaction update error:', err);
        }
      }
    )?.subscribe();

  return () => supabase?.removeChannel(channel);
}
